import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';

const PHASES = [
  { name: 'Foundation',          range: [1, 25] },
  { name: 'Primary Healing',     range: [26, 50] },
  { name: 'Integration',         range: [51, 75] },
  { name: 'Service & Abundance', range: [76, 100] },
];

const getPhase = (square) => PHASES.find(p => square >= p.range[0] && square <= p.range[1]) ?? PHASES[0];

function MiniBoard({ currentSquare, boardData }) {
  const mechanics = boardData?.snakes_and_ladders ?? [];
  const squares   = boardData?.squares ?? [];

  const snakeMap = {};
  const ladderMap = {};
  mechanics.forEach(m => {
    const sq = m.fromSquare ?? m.from;
    if (!sq) return;
    if (m.type === 'snake') snakeMap[sq] = m;
    else ladderMap[sq] = m;
  });

  const currentSquareData = squares.find(s => s.position === currentSquare);

  const phaseBg = (sq) => {
    if (sq <= 25)  return 'rgba(201,146,10,0.07)';
    if (sq <= 50)  return 'rgba(120,160,255,0.07)';
    if (sq <= 75)  return 'rgba(180,120,255,0.07)';
    return 'rgba(80,210,140,0.07)';
  };

  const getSquareNum = (displayRow, col) => {
    const boardRow = 9 - displayRow;
    if (boardRow % 2 === 0) return boardRow * 10 + col + 1;
    return boardRow * 10 + (9 - col) + 1;
  };

  const snakes  = mechanics.filter(m => m.type === 'snake');
  const ladders = mechanics.filter(m => m.type === 'ladder');

  return (
    <div>
      {/* Current square callout */}
      {currentSquareData && (
        <div style={{ background: 'rgba(201,146,10,0.1)', border: '1px solid rgba(201,146,10,0.25)', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px' }}>
          <div style={{ fontSize: '10px', color: '#C9920A', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '3px' }}>
            You are here
          </div>
          <div style={{ fontSize: '14px', color: '#F5F0E8', fontWeight: '600' }}>
            {currentSquareData.title}
          </div>
          {currentSquareData.desc && (
            <div style={{ fontSize: '12px', color: 'rgba(245,240,232,0.5)', marginTop: '3px', lineHeight: '1.4' }}>
              {currentSquareData.desc}
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {Array.from({ length: 10 }, (_, displayRow) => (
          <div key={displayRow} style={{ display: 'flex', gap: '2px' }}>
            {Array.from({ length: 10 }, (_, col) => {
              const sq = getSquareNum(displayRow, col);
              const isCurrent = sq === currentSquare;
              const isSnake   = !!snakeMap[sq];
              const isLadder  = !!ladderMap[sq];
              return (
                <div key={sq} style={{
                  flex: 1, height: '26px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isSnake || isLadder ? '11px' : '7px',
                  fontWeight: '700', borderRadius: '3px',
                  background: isCurrent ? '#C9920A'
                    : isSnake  ? 'rgba(220,60,60,0.22)'
                    : isLadder ? 'rgba(60,200,100,0.18)'
                    : phaseBg(sq),
                  color: isCurrent ? '#0D0D2B'
                    : isSnake  ? 'rgba(255,110,110,0.9)'
                    : isLadder ? 'rgba(80,220,130,0.9)'
                    : 'rgba(245,240,232,0.28)',
                  border: isCurrent
                    ? '2px solid #C9920A'
                    : isSnake  ? '1px solid rgba(220,60,60,0.35)'
                    : isLadder ? '1px solid rgba(60,200,100,0.35)'
                    : '1px solid rgba(255,255,255,0.04)',
                }}>
                  {isCurrent ? sq : isSnake ? '🐍' : isLadder ? '🪜' : sq}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Phase legend */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
        {[
          { label: 'Foundation',  bg: 'rgba(201,146,10,0.3)',  range: '1–25' },
          { label: 'Healing',     bg: 'rgba(120,160,255,0.3)', range: '26–50' },
          { label: 'Integration', bg: 'rgba(180,120,255,0.3)', range: '51–75' },
          { label: 'Service',     bg: 'rgba(80,210,140,0.3)',  range: '76–100' },
        ].map(p => (
          <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'rgba(245,240,232,0.45)' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: p.bg }} />
            {p.label} ({p.range})
          </div>
        ))}
      </div>

      {/* Snakes list */}
      {snakes.length > 0 && (
        <div style={{ marginTop: '18px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: 'rgba(220,100,100,0.85)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
            Snakes on your path
          </div>
          {snakes.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '10px', color: 'rgba(220,100,100,0.7)', fontWeight: '700', minWidth: '36px', paddingTop: '1px' }}>
                Sq {m.fromSquare ?? m.from}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: 'rgba(245,240,232,0.75)', fontWeight: '600', marginBottom: '2px' }}>{m.reason}</div>
                {m.description && <div style={{ fontSize: '11px', color: 'rgba(245,240,232,0.4)', lineHeight: '1.4' }}>{m.description}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ladders list */}
      {ladders.length > 0 && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: 'rgba(80,220,130,0.85)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
            Ladders ahead
          </div>
          {ladders.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '10px', color: 'rgba(80,220,130,0.7)', fontWeight: '700', minWidth: '36px', paddingTop: '1px' }}>
                Sq {m.fromSquare ?? m.from}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: 'rgba(245,240,232,0.75)', fontWeight: '600', marginBottom: '2px' }}>{m.reason}</div>
                {m.description && <div style={{ fontSize: '11px', color: 'rgba(245,240,232,0.4)', lineHeight: '1.4' }}>{m.description}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {mechanics.length === 0 && (
        <div style={{ marginTop: '12px', fontSize: '11px', color: 'rgba(245,240,232,0.3)', fontStyle: 'italic' }}>
          Complete the board questionnaire to unlock personalized snakes and ladders.
        </div>
      )}
    </div>
  );
}

export default function UserProfile({ currentSquare, hasPersonalizedBoard, onClose, onGoToQuestionnaire }) {
  const { currentUser, signOut } = useAuth();
  const [streak, setStreak]         = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [boardData, setBoardData]   = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }
    Promise.all([
      getDoc(doc(db, 'users', currentUser.uid, 'progress', 'data')),
      getDoc(doc(db, 'users', currentUser.uid, 'board', 'data')),
    ]).then(([progressSnap, boardSnap]) => {
      if (progressSnap.exists()) {
        const d = progressSnap.data();
        setStreak(d.streakCount ?? 0);
        setMilestones(d.milestonesAwarded ?? []);
      }
      if (boardSnap.exists()) {
        setBoardData(boardSnap.data());
      }
    }).catch(console.error).finally(() => setLoading(false));
  }, [currentUser]);

  const handleLogout = async () => {
    try { await signOut(); } catch (err) { console.error('Logout error:', err); }
  };

  const phase = getPhase(currentSquare ?? 0);
  const progressPct = Math.min(Math.round(((currentSquare ?? 0) / 100) * 100), 100);
  const isMoksha = currentSquare >= 100;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Your Journey</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button style={styles.logoutBtn} onClick={handleLogout}>Sign out</button>
            <button style={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        {loading ? (
          <p style={styles.loading}>Loading...</p>
        ) : (
          <>
            {/* Board position */}
            <div style={styles.section}>
              <div style={styles.positionRow}>
                <span style={styles.positionNum}>Square {currentSquare ?? 0}</span>
                <span style={styles.positionPhase}>{isMoksha ? 'Moksha' : phase.name}</span>
              </div>
              <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
              </div>
              <p style={styles.progressLabel}>{progressPct}% of the board complete</p>
            </div>

            {/* Visual board */}
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Journey Map</div>
              <MiniBoard currentSquare={currentSquare ?? 0} boardData={boardData} />
            </div>

            {/* Streak */}
            <div style={styles.section}>
              <div style={styles.streakRow}>
                <div>
                  <div style={styles.streakNum}>{streak}</div>
                  <div style={styles.streakLabel}>
                    {streak === 1 ? 'day of showing up' : 'days of showing up'}
                  </div>
                </div>
                <div style={styles.streakMsg}>
                  {streak === 0 ? "Your journey continues today." :
                   streak < 7  ? "Great start. Keep going." :
                   streak < 30 ? "Consistent. This is working." :
                   streak < 60 ? "One month. That's remarkable." :
                                 "You've made this a practice."}
                </div>
              </div>
            </div>

            {/* Achievements */}
            {milestones.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionLabel}>Milestones</div>
                <div style={styles.milestonesRow}>
                  {milestones.map(m => (
                    <div key={m} style={styles.milestoneBadge}>
                      <span style={styles.milestoneDays}>{m}</span>
                      <span style={styles.milestoneDaysLabel}>days</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Design board nudge */}
            {!hasPersonalizedBoard && (
              <div style={styles.nudgeBox}>
                <p style={styles.nudgeText}>
                  Personalize your board for a journey built around your specific challenges and goals.
                </p>
                <button style={styles.nudgeBtn} onClick={() => { onClose(); onGoToQuestionnaire?.(); }}>
                  Design my personal board
                </button>
              </div>
            )}
          </>
        )}

        <button style={styles.backBtn} onClick={onClose}>Back to practice</button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(13,13,43,0.95)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  card: {
    background: '#1E1145', border: '1px solid rgba(201,146,10,0.25)',
    borderRadius: '20px', padding: '28px 24px',
    width: '100%', maxWidth: '440px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  headerTitle: { fontFamily: '"Cinzel", serif', fontSize: '18px', fontWeight: '700', color: '#F5F0E8', margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(245,240,232,0.4)', fontSize: '18px', cursor: 'pointer', padding: '4px' },
  logoutBtn: {
    background: 'none', border: '1px solid rgba(245,240,232,0.15)',
    borderRadius: '6px', color: 'rgba(245,240,232,0.45)', fontSize: '12px',
    cursor: 'pointer', padding: '4px 10px', fontFamily: '"Lato", sans-serif',
  },
  loading: { color: 'rgba(245,240,232,0.4)', textAlign: 'center', padding: '20px 0' },

  section: { marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid rgba(245,240,232,0.07)' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', color: 'rgba(201,146,10,0.7)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' },

  positionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' },
  positionNum: { fontFamily: '"Cinzel", serif', fontSize: '22px', fontWeight: '700', color: '#F5F0E8' },
  positionPhase: { fontSize: '13px', color: '#C9920A', fontWeight: '600' },
  progressTrack: { height: '6px', background: 'rgba(201,146,10,0.15)', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' },
  progressFill: { height: '100%', background: '#C9920A', borderRadius: '3px', transition: 'width 0.6s ease' },
  progressLabel: { fontSize: '12px', color: 'rgba(245,240,232,0.4)', margin: 0 },

  streakRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  streakNum: { fontFamily: '"Cinzel", serif', fontSize: '36px', fontWeight: '700', color: '#C9920A', lineHeight: 1 },
  streakLabel: { fontSize: '12px', color: 'rgba(245,240,232,0.45)', marginTop: '4px' },
  streakMsg: { flex: 1, fontSize: '14px', color: 'rgba(245,240,232,0.6)', lineHeight: '1.5', fontStyle: 'italic' },

  milestonesRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  milestoneBadge: {
    width: '60px', height: '60px', borderRadius: '50%',
    background: 'rgba(201,146,10,0.12)', border: '1px solid rgba(201,146,10,0.3)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  },
  milestoneDays: { fontFamily: '"Cinzel", serif', fontSize: '18px', fontWeight: '700', color: '#C9920A', lineHeight: 1 },
  milestoneDaysLabel: { fontSize: '10px', color: 'rgba(245,240,232,0.4)', marginTop: '2px' },

  nudgeBox: {
    background: 'rgba(201,146,10,0.06)', border: '1px solid rgba(201,146,10,0.2)',
    borderRadius: '12px', padding: '16px', marginBottom: '16px',
  },
  nudgeText: { fontSize: '13px', color: 'rgba(245,240,232,0.55)', lineHeight: '1.5', marginTop: 0, marginBottom: '12px' },
  nudgeBtn: {
    width: '100%', padding: '11px', background: 'transparent',
    border: '1px solid rgba(201,146,10,0.4)', borderRadius: '10px',
    fontSize: '13px', fontWeight: '600', color: '#C9920A', cursor: 'pointer',
  },

  backBtn: {
    width: '100%', padding: '13px', background: '#C9920A',
    border: 'none', borderRadius: '12px', fontSize: '15px',
    fontWeight: '700', color: '#0D0D2B', cursor: 'pointer',
    fontFamily: '"Lato", sans-serif', marginTop: '4px',
  },
};
